import type { ReactNode } from "react";
import {
  formatCombination,
  formatCombinationWithElement,
  formatGrowStage,
  formatSenSha,
  formatThreeCombination,
  formatYiJings,
} from "./bazi.format";
import type { BaziChart, Pillar } from "./types";

// Left to right as the original page laid them out: the four birth pillars, then the three extra ones.
const COLUMNS = [
  { key: "hourPillar", label: "Hour" },
  { key: "dayPillar", label: "Day" },
  { key: "monthPillar", label: "Month" },
  { key: "yearPillar", label: "Year" },
  { key: "taiYuanPillar", label: "Tai Yuan" },
  { key: "mingGongPillar", label: "Ming Gong" },
  { key: "luckPillar", label: "Luck" },
  { key: "annualPillar", label: "Annual" },
] as const;

type Row = { label: string; cell: (pillar: Pillar, column: (typeof COLUMNS)[number]["key"]) => ReactNode };

function text(label: string, cell: (pillar: Pillar) => string): Row {
  return { label, cell };
}

const HIDDEN_STEMS: Row = {
  label: "Hidden Stems",
  cell: (pillar) =>
    pillar.earthlyBranch.hiddenStems.map((hidden, index) => (
      <span key={hidden.id}>
        {index > 0 && ", "}
        {hidden.name.toUpperCase()} (<small>{hidden.tenGod.nameEn}</small>) - {hidden.weight}
      </span>
    )),
};

const HEAVENLY_STEM: Row = {
  label: "Heavenly Stem",
  // The Day pillar's stem is the Day Master itself, so it has no Ten God.
  cell: (pillar, column) =>
    column === "dayPillar" ? (
      pillar.heavenlyStem.name.toUpperCase()
    ) : (
      <>
        {pillar.heavenlyStem.name.toUpperCase()} (<small>{pillar.heavenlyStem.tenGod.nameEn}</small>)
      </>
    ),
};

// Under the tall "Pillar" cell: these three rows carry no label of their own (as on the original page).
const PILLAR_ROWS: Row[] = [
  HEAVENLY_STEM,
  text("Earthly Branch", (pillar) => pillar.earthlyBranch.name.toUpperCase()),
  HIDDEN_STEMS,
];

const ROWS: Row[] = [
  // The original page had this row but its server never sent a value for it.
  { label: "DM Strength", cell: () => "-" },
  text("Grow Stage", (pillar) => formatGrowStage(pillar.growStage)),
  text("Na Yin", (pillar) => pillar.naYin),
  text("Yi Jing", (pillar) => formatYiJings(pillar.yiJings)),
  text("HS Combination", (pillar) => formatCombinationWithElement(pillar.hsCombinations, "hs1", "hs2")),
  text("HS Clash", (pillar) => formatCombination(pillar.hsClashes, "hs1", "hs2")),
  text("EB Combination", (pillar) => formatCombinationWithElement(pillar.ebCombinations, "eb1", "eb2")),
  text("EB Three Combination", (pillar) => formatThreeCombination(pillar.ebThreeCombinations)),
  text("EB Half Combination", (pillar) => formatCombinationWithElement(pillar.ebHalfCombination, "eb1", "eb2")),
  text("EB Directional Combination", (pillar) => formatThreeCombination(pillar.ebDirCombinations)),
  text("EB Clash", (pillar) => formatCombination(pillar.ebClashes, "eb1", "eb2")),
  text("EB Bullying Punishment", (pillar) => formatCombination(pillar.ebBullyingPunishments, "eb1", "eb2")),
  text("EB Self Punishment", (pillar) => formatCombination(pillar.ebSelfPunishments, "eb1", "eb2")),
  text("EB Ungrateful Punishment", (pillar) => formatCombination(pillar.ebUngratefulPunishments, "eb1", "eb2")),
  text("EB Uncivilized Punishment", (pillar) => formatCombination(pillar.ebUncivilizedPunishments, "eb1", "eb2")),
  text("EB Harm", (pillar) => formatCombination(pillar.ebHarms, "eb1", "eb2")),
  text("EB Destruction", (pillar) => formatCombination(pillar.ebDestructions, "eb1", "eb2")),
  text("Fu Yin", (pillar) => formatCombination(pillar.fuYins, "a", "b")),
  text("Fan Yin", (pillar) => formatCombination(pillar.fanYins, "a", "b")),
  text("Sen Sha Day Stem", (pillar) => formatSenSha(pillar.senSha.dayStemBased)),
  text("Sen Sha Day Branch", (pillar) => formatSenSha(pillar.senSha.dayBranchBased)),
  text("Sen Sha Month Branch", (pillar) => formatSenSha(pillar.senSha.monthBranchBased)),
  text("Sen Sha Year Branch", (pillar) => formatSenSha(pillar.senSha.yearBranchBased)),
];

export function PillarsTable({ chart }: { chart: BaziChart }) {
  return (
    <div className="bazi-scroll">
      <table className="bazi-table" aria-label="Pillars">
        <tbody>
          {/* One group on purpose: the Pillar cell spans the header row and the three rows below it, and a
              rowspan cannot cross from a <thead> into a <tbody>. */}
          <tr>
            <th scope="col" rowSpan={PILLAR_ROWS.length + 1} className="bazi-pillar-label">
              Pillar
            </th>
            {COLUMNS.map((column) => (
              <th key={column.key} scope="col" className="bazi-column-head">
                {column.label}
              </th>
            ))}
          </tr>
          {PILLAR_ROWS.map((row) => (
            <tr key={row.label} aria-label={row.label}>
              {COLUMNS.map((column) => (
                <td key={column.key}>{row.cell(chart[column.key], column.key)}</td>
              ))}
            </tr>
          ))}
          {ROWS.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {row.label === "DM Strength" ? (
                <td colSpan={COLUMNS.length}>{row.cell(chart.dayPillar, "dayPillar")}</td>
              ) : (
                COLUMNS.map((column) => <td key={column.key}>{row.cell(chart[column.key], column.key)}</td>)
              )}
            </tr>
          ))}
          <tr>
            <th scope="row">Zodiac</th>
            <td colSpan={COLUMNS.length}>{chart.zodiac}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
